import type { Decor, ObjectId, Rect } from '../world/mapdef';
import { OUTLINE, block, ellipse, rect, shadow, type Ctx } from './paint';
import { wind } from './nature';

type Drawer = (c: Ctx, r: Rect, t: number) => void;

function counterTop(c: Ctx, r: Rect, fill: string, light: string, dark: string): void {
  shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 1, 3);
  block(c, r.x, r.y - 10, r.w, r.h + 10, fill, light, dark);
  rect(c, 'rgba(0,0,0,0.18)', r.x + 1, r.y - 1, r.w - 2, 1);
}

function net(c: Ctx, r: Rect, t: number, post: string, mesh: string): void {
  const sway = wind.at(r.x, r.y) * 0.6 + Math.sin(t * 2) * 0.2;
  rect(c, 'rgba(0,0,0,0.25)', r.x, r.y + 3, r.w, 2);
  for (const px of [r.x - 2, r.x + r.w]) {
    rect(c, OUTLINE, px, r.y - 16, 3, 19);
    rect(c, post, px + 0.5, r.y - 15.5, 2, 18);
  }
  c.strokeStyle = mesh;
  c.lineWidth = 0.5;
  c.beginPath();
  for (let x = r.x; x <= r.x + r.w; x += 3) {
    c.moveTo(x, r.y - 12);
    c.lineTo(x + sway, r.y - 2);
  }
  for (let y = r.y - 12; y <= r.y - 2; y += 2.5) {
    c.moveTo(r.x, y);
    c.lineTo(r.x + r.w, y);
  }
  c.stroke();
  rect(c, '#ffffff', r.x, r.y - 13, r.w, 1.5);
}

export const OBJ2: Partial<Record<ObjectId, Drawer>> = {
  nurseDesk(c, r) {
    counterTop(c, r, '#f4f1ea', '#ffffff', '#cfc8ba');
    block(c, r.x + 4, r.y - 18, 12, 9, '#2b3440');
    rect(c, '#7fe0b0', r.x + 6, r.y - 16, 8, 1);
    c.strokeStyle = '#3fd38a';
    c.lineWidth = 0.6;
    c.beginPath();
    c.moveTo(r.x + 6, r.y - 13);
    c.lineTo(r.x + 8, r.y - 13);
    c.lineTo(r.x + 9, r.y - 16);
    c.lineTo(r.x + 10, r.y - 11);
    c.lineTo(r.x + 11, r.y - 13);
    c.lineTo(r.x + 14, r.y - 13);
    c.stroke();
    block(c, r.x + 22, r.y - 14, 10, 6, '#ffffff');
    rect(c, '#e2493b', r.x + 26, r.y - 13, 2, 4);
    rect(c, '#e2493b', r.x + 25, r.y - 12, 4, 2);
  },
  pool(c, r, t) {
    // Light ripples dancing over the water.
    for (let i = 0; i < 6; i++) {
      const k = (t * 0.35 + i / 6) % 1;
      const cx = r.x + 16 + ((i * 53) % (r.w - 30)), cy = r.y + 14 + ((i * 37) % (r.h - 26));
      c.strokeStyle = `rgba(255,255,255,${0.45 * (1 - k)})`;
      c.lineWidth = 0.6;
      c.beginPath();
      c.ellipse(cx, cy, 2 + k * 9, 1 + k * 4, 0, 0, Math.PI * 2);
      c.stroke();
    }
    // Lane ropes with floats.
    for (let y = r.y + 32; y < r.y + r.h - 8; y += 32) {
      for (let x = r.x + 2; x < r.x + r.w - 2; x += 4) ellipse(c, (x / 4) % 2 ? '#e2493b' : '#ffffff', x, y + Math.sin(t * 2 + x * 0.1) * 0.4, 1.4, 1);
    }
  },
  gymNet(c, r, t) {
    net(c, r, t, '#9aa6ae', 'rgba(255,255,255,0.75)');
  },
  tennisNet(c, r, t) {
    net(c, r, t, '#2b3440', 'rgba(255,255,255,0.7)');
  },
  volleyNet(c, r, t) {
    net(c, r, t, '#c08048', 'rgba(255,255,255,0.8)');
  },
  fountain: fountain,
  mallFountain: fountain,
  cafeCounter(c, r) {
    counterTop(c, r, '#8a5530', '#a36a40', '#6b3f22');
    block(c, r.x + 4, r.y - 24, 14, 14, '#3c4652', '#56616e');
    rect(c, '#c9d2d8', r.x + 7, r.y - 20, 8, 2);
    block(c, r.x + 24, r.y - 16, 6, 6, '#ffffff');
    block(c, r.x + 32, r.y - 16, 6, 6, '#f4c27a');
    block(c, r.x + 44, r.y - 20, 14, 10, 'rgba(220,240,255,0.6)');
    for (let i = 0; i < 3; i++) ellipse(c, ['#f4c27a', '#c97a3a', '#ffd34e'][i], r.x + 47 + i * 4, r.y - 13, 1.6, 1.2);
  },
  giftCounter(c, r) {
    counterTop(c, r, '#ff9fc0', '#ffd1dd', '#d97a99');
    for (let i = 0; i < 3; i++) {
      const x = r.x + 4 + i * 14;
      block(c, x, r.y - 18, 10, 9, ['#3a8fd9', '#ffd34e', '#57c278'][i]);
      rect(c, '#e2493b', x + 4, r.y - 18, 2, 9);
      rect(c, '#e2493b', x, r.y - 14, 10, 2);
    }
  },
  deptCounter(c, r) {
    counterTop(c, r, '#2f3442', '#4a5060', '#1d2028');
    block(c, r.x + 6, r.y - 18, 14, 10, '#2b3440');
    rect(c, '#57c278', r.x + 8, r.y - 16, 10, 4);
    block(c, r.x + 26, r.y - 14, 16, 6, '#ffffff');
  },
  restCounter(c, r) {
    counterTop(c, r, '#b8814e', '#d9a876', '#8a5c34');
    for (let i = 0; i < 3; i++) {
      ellipse(c, '#ffffff', r.x + 10 + i * 16, r.y - 12, 6, 2.5);
      ellipse(c, ['#e8735a', '#ffd34e', '#57c278'][i], r.x + 10 + i * 16, r.y - 13, 4, 1.6);
    }
    block(c, r.x + r.w - 12, r.y - 26, 10, 14, '#2f3442');
    rect(c, '#ffd34e', r.x + r.w - 10, r.y - 23, 6, 1);
    rect(c, '#ffd34e', r.x + r.w - 10, r.y - 20, 6, 1);
  },
};

function fountain(c: Ctx, r: Rect, t: number): void {
  const cx = r.x + r.w / 2, by = r.y + r.h;
  shadow(c, cx, by, r.w / 2 + 4, 4);
  ellipse(c, OUTLINE, cx, by - 6, r.w / 2 + 3, 7);
  ellipse(c, '#d6d0c4', cx, by - 6, r.w / 2 + 2, 6);
  ellipse(c, '#5ec8e6', cx, by - 7, r.w / 2 - 1, 4);
  block(c, cx - 2, by - 24, 4, 18, '#d6d0c4', '#ffffff', '#a9a396');
  ellipse(c, '#d6d0c4', cx, by - 24, 6, 2);
  // Animated jets and droplets.
  for (let i = 0; i < 10; i++) {
    const k = (t * 1.2 + i / 10) % 1;
    const a = (i / 10) * Math.PI * 2;
    const dx = Math.cos(a) * k * 9, dy = -Math.sin(k * Math.PI) * 10;
    c.fillStyle = `rgba(200,240,255,${0.9 - k * 0.6})`;
    c.fillRect(cx + dx, by - 25 + dy + k * 14, 1, 1.5);
  }
  for (let i = 0; i < 3; i++) {
    const k = (t * 0.6 + i / 3) % 1;
    c.strokeStyle = `rgba(255,255,255,${0.5 * (1 - k)})`;
    c.lineWidth = 0.5;
    c.beginPath();
    c.ellipse(cx, by - 7, 3 + k * (r.w / 2 - 4), 1 + k * 3, 0, 0, Math.PI * 2);
    c.stroke();
  }
}

/** Decor kinds added with the new places. Returns false if the kind isn't handled here. */
export function drawDecor2(c: Ctx, d: Decor, t: number): boolean {
  const { x, y } = d;
  switch (d.kind) {
    case 'tennisNet':
    case 'volleyNet':
    case 'fountain':
      return true; // drawn by their interactive objects
    case 'signSports':
    case 'signMall':
      rect(c, OUTLINE, x - 1, y - 22, 3, 22);
      block(c, x - 18, y - 32, 36, 12, d.kind === 'signSports' ? '#3a8fd9' : '#e8735a', '#ffffff');
      rect(c, '#fff6dc', x - 14, y - 28, 28, 4);
      return true;
    case 'parkedCar': {
      const col = d.variant ? '#3a8fd9' : '#e2493b';
      shadow(c, x + 15, y + 14, 18, 4);
      block(c, x, y + 2, 30, 12, col);
      block(c, x + 6, y - 6, 18, 9, col);
      rect(c, '#9fd3f0', x + 8, y - 4, 6, 5);
      rect(c, '#9fd3f0', x + 16, y - 4, 6, 5);
      block(c, x + 3, y + 12, 6, 4, '#1b1d24');
      block(c, x + 21, y + 12, 6, 4, '#1b1d24');
      return true;
    }
    case 'hay':
      shadow(c, x, y, 13, 3);
      block(c, x - 12, y - 10, 24, 10, '#e2c45a', '#f2dc84', '#b89a3a');
      for (let i = -10; i < 12; i += 3) rect(c, 'rgba(140,110,40,0.4)', x + i, y - 9, 0.75, 8);
      return true;
    case 'trough':
      shadow(c, x + 13, y, 14, 3);
      block(c, x, y - 8, 26, 8, '#8a5530', '#a36a40', '#6b3f22');
      rect(c, '#7fc8f0', x + 2, y - 7, 22, 3);
      return true;
    case 'gymCourt': {
      const w = d.w ?? 200, h = d.h ?? 180;
      c.strokeStyle = 'rgba(255,255,255,0.85)';
      c.lineWidth = 1;
      c.strokeRect(x + 0.5, y + 0.5, w, h);
      c.beginPath();
      c.moveTo(x, y + h / 2);
      c.lineTo(x + w, y + h / 2);
      c.stroke();
      c.strokeStyle = 'rgba(255,210,80,0.8)';
      c.strokeRect(x + 12, y + 12, w - 24, h - 24);
      return true;
    }
    case 'scoreboard':
      block(c, x, y + 1, 48, 13, '#1b1d24');
      c.fillStyle = '#ff5a3a';
      c.font = '800 8px monospace';
      c.textAlign = 'center';
      c.fillText(`${Math.floor(t / 7) % 25}`, x + 12, y + 10);
      c.fillStyle = '#ffd34e';
      c.fillText(`${Math.floor(t / 9) % 25}`, x + 36, y + 10);
      return true;
    case 'lifeguardChair':
      shadow(c, x, y, 8, 3);
      rect(c, OUTLINE, x - 6, y - 26, 2, 26);
      rect(c, OUTLINE, x + 4, y - 26, 2, 26);
      block(c, x - 7, y - 30, 14, 5, '#ffffff', undefined, '#cfd8de');
      block(c, x - 8, y - 38, 16, 8, '#e2493b');
      return true;
    case 'benchIndoor':
      shadow(c, x, y, 22, 3);
      block(c, x - 22, y - 7, 44, 6, '#4f8fc0', '#78aacb', '#3a6f99');
      rect(c, OUTLINE, x - 20, y - 1, 2, 2);
      rect(c, OUTLINE, x + 18, y - 1, 2, 2);
      return true;
    case 'ballCart':
      shadow(c, x, y, 9, 3);
      block(c, x - 8, y - 12, 16, 12, '#9aa6ae', '#c9d2d8');
      for (let i = 0; i < 4; i++) ellipse(c, i % 2 ? '#ffd34e' : '#3a8fd9', x - 5 + i * 3.5, y - 13, 2.2, 2.2);
      return true;
    case 'menuBoard':
      block(c, x, y, 26, 13, '#2a2420');
      for (let i = 0; i < 4; i++) rect(c, i % 2 ? '#ffd34e' : '#fff6dc', x + 3, y + 3 + i * 2.5, 12 + (i % 3) * 3, 1);
      return true;
    case 'giftShelf': {
      const w = d.w ?? 48;
      shadow(c, x, y, w / 2, 3);
      block(c, x - w / 2, y - 30, w, 30, '#f4e2c8', '#fff4e0', '#d8c4a4');
      for (let j = 0; j < 3; j++) {
        rect(c, '#c4a676', x - w / 2 + 2, y - 21 + j * 9, w - 4, 1);
        for (let i = 0; i < w - 10; i += 8) {
          const col = ['#e2493b', '#3a8fd9', '#ffd34e', '#57c278', '#ff9fc0'][(i / 8 + j + (d.variant ?? 0)) % 5];
          block(c, x - w / 2 + 4 + i, y - 27 + j * 9, 6, 6, col);
        }
      }
      return true;
    }
    case 'clothesRack': {
      shadow(c, x, y, 14, 3);
      rect(c, OUTLINE, x - 13, y - 26, 26, 2);
      rect(c, OUTLINE, x - 1, y - 26, 2, 26);
      const cols = d.variant ? ['#e2493b', '#ffd34e', '#57c278', '#9b5fc0'] : ['#3a8fd9', '#ff9fc0', '#f4f1ea', '#2f3442'];
      cols.forEach((col, i) => {
        const sw = Math.sin(t * 1.5 + i) * 0.4;
        block(c, x - 12 + i * 6 + sw, y - 24, 6, 14, col);
      });
      return true;
    }
    case 'mannequin':
      shadow(c, x, y, 5, 2);
      rect(c, OUTLINE, x - 0.5, y - 6, 1, 6);
      block(c, x - 5, y - 22, 10, 16, '#e8735a', '#f29b84');
      ellipse(c, '#e8e2d6', x, y - 25, 3.5, 3.5);
      return true;
    case 'easelDeco':
      shadow(c, x, y, 9, 3);
      rect(c, OUTLINE, x - 6, y - 22, 2, 22);
      rect(c, OUTLINE, x + 4, y - 22, 2, 22);
      block(c, x - 9, y - 30, 18, 14, '#fff9ea');
      rect(c, d.variant ? '#9b5fc0' : '#3a8fd9', x - 7, y - 28, 14, 6);
      ellipse(c, '#ffd34e', x + 3, y - 22, 2.5, 2.5);
      return true;
    case 'paintShelf': {
      const w = d.w ?? 48;
      shadow(c, x, y, w / 2, 3);
      block(c, x - w / 2, y - 22, w, 22, '#8a5530', '#a36a40', '#6b3f22');
      for (let i = 0; i < w - 8; i += 5) block(c, x - w / 2 + 3 + i, y - 18, 4, 6, ['#e2493b', '#ffd34e', '#3a8fd9', '#57c278', '#ff9f43'][(i / 5) % 5]);
      return true;
    }
    case 'nurseBed':
      shadow(c, x, y, 16, 4);
      block(c, x - 14, y - 22, 28, 22, '#c9d2d8', '#eef3f6', '#a2adb5');
      block(c, x - 12, y - 20, 24, 18, '#ffffff');
      block(c, x - 10, y - 19, 20, 5, '#f4f1ea');
      block(c, x - 12, y - 12, 24, 10, '#9fd3f0', '#bfe6ff');
      return true;
    case 'medCabinet':
      shadow(c, x, y, 16, 3);
      block(c, x - 15, y - 30, 30, 30, '#ffffff', undefined, '#cfd8de');
      rect(c, 'rgba(160,210,240,0.5)', x - 13, y - 28, 26, 14);
      for (let i = 0; i < 4; i++) block(c, x - 12 + i * 6, y - 26, 4, 5, ['#e2493b', '#57c278', '#ffd34e', '#3a8fd9'][i]);
      rect(c, '#e2493b', x - 2, y - 10, 4, 6);
      rect(c, '#e2493b', x - 4, y - 8, 8, 2);
      return true;
    case 'curtainScreen':
      shadow(c, x, y, 9, 2);
      for (let i = 0; i < 3; i++) block(c, x - 9 + i * 6, y - 26, 6, 24, '#c9f2e0', '#e6fff4');
      return true;
    case 'musicStands':
      for (let i = 0; i < 3; i++) {
        const sx = x - 16 + i * 16;
        rect(c, OUTLINE, sx, y - 14, 1, 14);
        block(c, sx - 5, y - 20, 10, 7, '#2b3440');
        rect(c, '#ffffff', sx - 3, y - 18, 6, 3);
      }
      return true;
    case 'xylophone':
      shadow(c, x, y, 14, 3);
      block(c, x - 14, y - 8, 28, 8, '#6b3f22');
      for (let i = 0; i < 7; i++) block(c, x - 13 + i * 3.8, y - 12 + i * 0.3, 3.5, 9 - i * 0.6, ['#e2493b', '#ff9f43', '#ffd34e', '#57c278', '#3a8fd9', '#5a5ad9', '#9b5fc0'][i]);
      return true;
    default:
      return false;
  }
}
